<template>
    <Portal to="modal">
        <ModalContainer key="emailAuthModal">
            <div class="email-auth">
                <img class="email-auth__sparkles" src="@/assets/signIn/Sign-in_Link-Sparkles.png">
                <img class="email-auth__mobile-sparkles" src="@/assets/signIn/Sign-in_mobile-code-sparkles.svg">
                <div class="email-auth__header">
                    <Icon
                        class="email-auth__back-arrow"
                        type="arrow"
                        tabindex="0"
                        role="button"
                        aria-label="Back"
                        @keyup.enter="clickBackArrow"
                        @click="clickBackArrow"
                        @mousedown.prevent
                        @keydown.enter="clickBackArrow"
                    />
                    <div v-if="!isLoading" class="email-auth__link-sent">
                        <Icon class="email-auth__link-sent-check" type="check" />
                        <span role="alert" class="email-auth__link-sent-text">Link Sent</span>
                    </div>
                </div>
                <EmailAuthForm
                    v-model="code"
                    class="email-auth__form"
                    :email="email"
                    :parent-error="errorMessage"
                    :parent-is-loading="isLoading"
                    @submit="submitCode"
                    @resend="resendEmail"
                />
            </div>
        </ModalContainer>
    </Portal>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'

const {
    ModalContainer,
    Icon,
    EmailAuth: EmailAuthForm,
} = UIKit

const route = useRoute()
const router = useRouter()

const email = ref('')
const code = ref('')
const errorMessage = ref('')
const isLoading = ref(true)

onMounted(async () => {
    // if user logged in, check query email is same as user email and redirect to home
    if (userModule.state.user?.email === route.query?.email && route.query?.email) {
        if (route.query.redirect && typeof route.query.redirect === 'string') {
            const [ path, queryParams ] = route.query.redirect.split('?')
            const query = Object.fromEntries(new URLSearchParams(queryParams))
            router.push({ path, query })
        } else {
            router.push({ name: 'study' })
        }
    } else {
        await userModule.actions.signOut()
        if (typeof route.query.code === 'string') {
            const queryCode = route.query.code
            if (typeof Number(queryCode) === 'number' && queryCode.length === 6) {
                code.value = queryCode
            } else {
                errorMessage.value = `Invalid code: ${queryCode}. Code must be six digits`
            }
        }
        if (typeof route.query.email === 'string') {
            email.value = route.query.email

            if (code.value) {
                submitCode()
            }
        } else {
            router.push({
                name: 'sign-in',
            })
        }
    }

    setTimeout(() => {
        isLoading.value = false
    }, 500)
})

const clickBackArrow = () => {
    router.push({
        name: 'sign-in',
    })
}

const submitCode = async () => {
    errorMessage.value = ''
    isLoading.value = true
    try {
        const { redirect: magicRedirect } = await userModule.actions.signInWithCode({
            email: email.value,
            code: code.value,
        })
        if (route.query.redirect && typeof route.query.redirect === 'string') {
            const [ path, queryParams ] = route.query.redirect.split('?')
            const query = Object.fromEntries(new URLSearchParams(queryParams))
            router.push({ path, query })
        } else if (magicRedirect) {
            const [ path, queryParams ] = magicRedirect.split('?')
            const query = Object.fromEntries(new URLSearchParams(queryParams))
            router.push({ path, query })
        } else {
            router.push({ name: 'study' })
        }
    } catch (err) {
        errorMessage.value = 'That code wasn\'t right or it expired'
        isLoading.value = false
    }
    isLoading.value = false
}

const resendEmail = async () => {
    isLoading.value = true
    try {
        code.value = ''
        await userModule.actions.sendMagicEmail({
            email: email.value,
            route,
        })
    } catch (err) {
        errorMessage.value = 'We weren\'t able to send the link.'
    }
    setTimeout(() => {
        isLoading.value = false
    }, 500)
}
</script>

<style lang="scss" scoped>
.email-auth {
    position: relative;
    box-sizing: border-box;
    background-color: $floral-white;
    width: 661px;
    height: 503px;

    @include breakpoint(black-bear) {
        width: 100%;
        height: 673px;
    }

    &__sparkles {
        position: absolute;
        top: 86px;
        left: 105px;
        width: 484px;
        height: 291px;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__mobile-sparkles {
        display: none;

        @include breakpoint(black-bear) {
            display: initial;
            position: absolute;
            top: 42px;
            left: 50%;
            transform: translateX(-50%);
            width: 353px;
            height: 380px;
        }
    }

    &__header {
        position: absolute;
        top: 15px;
        display: flex;
        align-items: center;
        width: 100%;
        z-index: 2;
    }

    &__back-arrow {
        width: 18px;
        height: 24px;
        margin-left: 17px;
        padding: 0 4px;
        transform: rotate(180deg);
        color: $brand-blue;
        cursor: pointer;

        &:hover {
            color: $brand-black;
        }
    }

    &__link-sent {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        font-size: 14px;
        line-height: 17px;
        height: 19px;
        color: $slate-03;
    }

    &__link-sent-check {
        position: relative;
        top: 1px;
        width: 12px;
        height: 10px;
        margin-right: 7px;
    }

    &__form {
        width: 100%;
        position: absolute;
        top: 0;
        z-index: 1;
    }
}
</style>
